import { DivWithSpinner, IntegratedDialog } from "@tedivo/tedivo-ui";
import {
  FederationProvidersEnum,
  ICognitoUserWithOrgData,
  IDomainFederation,
  ISingleOrganizationAndUsers,
  TUserRole,
  systemRights,
} from "@tedivo/tvd-api-models";
import {
  IField as IFieldForm,
  IFields,
  IFormReturn,
  TedivoForm,
  translateTedivoForm,
} from "@tedivo/tedivo-form";
import {
  IOptionsStorage,
  ITable,
  ITableField,
  ITableOnClickOptions,
  SmartTable,
} from "@tedivo/tedivo-smart-table";
import createNotification, {
  createErrorNotification,
} from "../../../app/notifications/createNotification";
import { getTranslation, i18nReactive } from "../../../app/i18/i18tn";

import { EditDrawer } from "../../common/EditDrawer";
import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import ICustomError from "../../types/ICustomError";
import { IntegratedDialogError } from "@tedivo/tedivo-ui";
import Services from "../../../app/services";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button";
import SlDialog from "@shoelace-style/shoelace/dist/components/dialog/dialog";
import UserTypeEnum from "../../../app/enums/UserTypeEnum";
import { createTableUserStorageInstance } from "../../../app/preferences/createTableUserStorageInstance";
import { formatDateInCurrentTimezone } from "@tedivo/tedivo-pure-helpers";
import globalStore from "../../../app/stores/globalStore";
import goSquared from "../../../app/tracking/goSquared";
import { mergeUsersWithCurrentOrgData } from "../admin/organization-users.component";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import securityModule from "../../../app/security/SecurityModule";
import { setAppTitle } from "../../../app/setAppTitle";
import topMessageElement from "../../layout/top-tools/getTopMessageElement";
import { z } from "zod";

export class TVDMyOrganizationComponent extends HTMLElement {
  public static observedAttributes = [];

  private i18nCR: I18nComponentRegisterer;
  private dialogError: IntegratedDialogError;
  private wrapper: DivWithSpinner;
  private mainBox: HTMLDivElement;
  private deleteDialog: SlDialog;
  private editDrawer: EditDrawer;

  constructor() {
    super();
    this.i18nCR = new I18nComponentRegisterer(i18nReactive);
    this.dialogError = new IntegratedDialogError(
      this,
      false,
      getTranslation("general:common.close"),
    );
    this.wrapper = new DivWithSpinner();
    this.mainBox = document.createElement("div");
    this.mainBox.className = "oss-card";
    this.deleteDialog = document.createElement("sl-dialog");
    this.editDrawer = new EditDrawer();

    setAppTitle(getTranslation("menu:organization"));
  }

  connectedCallback() {
    goSquared.trackPage("My Organization");
    goSquared.addEvent("My Organization - Show page");

    const wrapper = this.wrapper,
      h1 = document.createElement("h1");

    wrapper.appendChild(h1);
    wrapper.appendChild(this.mainBox);

    this.appendChild(this.editDrawer.drawer);
    this.appendChild(wrapper);

    this.displayOrganizationUsers();

    this.i18nCR.addConsumer(
      topMessageElement.element,
      "menu:organization",
      "innerHTML",
    );

    this.i18nCR.addConsumer(h1, "general:users.users", "innerHTML");
  }

  private displayOrganizationUsers = () => {
    globalStore.touchLastUserInteraction();

    const orgId = securityModule.currentOrganizationId;
    if (!orgId) return;

    const wrapper = this.wrapper;
    const box = this.mainBox;
    const actionBox = document.createElement("div");
    actionBox.className = "table-action-box";

    removeChildren(box);

    if (
      !securityModule.planIsReadOnly &&
      securityModule.userHasPermission(
        systemRights.ORG.CanCreateMoreThanOneUser,
      ) &&
      securityModule.userHasPermission(systemRights.USER.CreateUser)
    ) {
      const actionAddUserButton = document.createElement("sl-button");
      actionAddUserButton.innerHTML = getTranslation(
        "general:users.adminCreate",
      );
      actionAddUserButton.variant = "primary";
      actionAddUserButton.addEventListener(
        "click",
        () => {
          this.openOrganizationUserEditModal();
        },
        false,
      );
      actionBox.appendChild(actionAddUserButton);
    }

    if (
      !securityModule.planIsReadOnly &&
      securityModule.userHasPermission(
        systemRights.USER.CanChangeOrganizationName,
      )
    ) {
      const actionEditOrgNameButton = document.createElement("sl-button");
      actionEditOrgNameButton.innerHTML = getTranslation(
        "general:organizations.changeOrganizationDetails",
      );
      actionEditOrgNameButton.variant = "primary";
      actionEditOrgNameButton.addEventListener(
        "click",
        async () => {
          actionEditOrgNameButton.loading = true;
          await this.openOrganizationEditModal({
            canChangeFederations:
              securityModule.currentOrganizationPlanId !== "FREE" &&
              securityModule.userHasPermission(
                systemRights.USER.UserCanChangeFederations,
              ),
          });
          actionEditOrgNameButton.loading = false;
        },
        false,
      );
      actionBox.appendChild(actionEditOrgNameButton);
    }

    wrapper.setLoading(true);
    Services.organizations
      .getSingleOrganizationAndUsers(orgId)
      .then((resp) => {
        if (resp.data) {
          const userOptionsStorage =
            createTableUserStorageInstance<ICognitoUserWithOrgData>(
              "myOrganizationUsersList",
            );

          box.appendChild(
            createOrgUsersTable({
              data: resp.data,
              userOptionsStorage,
              actionBox,
              onEditOrganizationUser: (o: ICognitoUserWithOrgData) => {
                this.openOrganizationUserEditModal({ ...o });
              },
              onDeleteOrganizationUser: this.deleteOrganizationUser,
              userType: securityModule.userRole,
              isReadOnly: securityModule.planIsReadOnly,
            }),
          );
        }
      })
      .catch((e) => {
        this.showError(e);
      })
      .finally(() => {
        wrapper.setLoading(false);
      });
  };

  disconnectedCallback() {
    this.i18nCR.disconnect();
  }

  showError = (e: ICustomError, data?: Record<string, unknown>) => {
    this.dialogError.show(
      getTranslation(`errors:errorHasOcurred`),
      getTranslation(typeof e === "string" ? e : e.translationKey, data),
    );
  };

  private openOrganizationUserEditModal = async (
    prevData?: ICognitoUserWithOrgData,
  ) => {
    const orgId = securityModule.currentOrganizationId;
    if (!orgId) return;

    const sub = prevData?.sub;
    let editForm: IFormReturn<ICognitoUserWithOrgData> | null = null;

    const drawer = this.editDrawer.getEditDrawer({
      title: getTranslation(
        prevData ? "general:users.adminEdit" : "general:users.adminCreate",
      ),
      showUnits: false,
      readOnlyMode: false,
      onSave: async () => {
        this.editDrawer.disabled = true;
        const valid = (await editForm?.submitFunction()) || false;
        this.editDrawer.disabled = false;

        if (valid) {
          this.displayOrganizationUsers();
        }

        return valid;
      },
    });

    const holder = document.createElement("div-spinner-element");
    holder.setLoading(true);
    drawer.appendChild(holder);
    drawer.show();

    if (sub && prevData) {
      const u = await Services.accounts.getUserDetailsByAdmin(
        orgId,
        prevData.mainIdentityId,
      );
      prevData.mfaOptions = u.data?.mfaOptions;
    }

    editForm = createUserEdit(
      this.editDrawer.submitButton,
      orgId,
      prevData,
      this.showError,
      securityModule.orgAllowedDomains,
    );

    if (editForm) {
      holder.appendChild(editForm.node);
    }

    holder.setLoading(false);
  };

  private openOrganizationEditModal = async ({
    canChangeFederations,
  }: {
    canChangeFederations: boolean;
  }) => {
    const myOrgFederationsFn:
      | undefined
      | (() => Promise<IDomainFederation[] | undefined>) = undefined;

    // if (canChangeFederations) {
    //   myOrgFederationsFn = async () =>
    //     (await Services.accounts.getOrgFederations()).data;
    // }

    const editForm = createOrganizationEditModal(
      this.editDrawer.submitButton,
      this.showError,
      canChangeFederations,
      myOrgFederationsFn,
    );

    if (editForm) {
      const drawer = this.editDrawer.getEditDrawer({
        title: getTranslation(
          "general:organizations.changeOrganizationDetails",
        ),
        showUnits: false,
        readOnlyMode: false,
        onSave: async () => {
          this.editDrawer.disabled = true;
          const valid = await editForm.submitFunction();
          this.editDrawer.disabled = false;

          if (valid) {
            this.displayOrganizationUsers();
          }

          return valid;
        },
      });

      drawer.appendChild(editForm.node);
      drawer.show();
    }
  };

  private deleteOrganizationUser = (
    orgId: string,
    dta: ICognitoUserWithOrgData,
  ) => {
    const dialog = this.deleteDialog;
    removeChildren(dialog);

    dialog.label = getTranslation("general:users.delete");

    const htmlContent = document.createElement("div");
    htmlContent.innerHTML = `${getTranslation(
      "general:users.delete",
    )}<br /><br /><strong>${dta.name} ${dta.familyName}</strong><br />${
      dta.email
    }`;

    const deleteBtn = document.createElement("sl-button");
    deleteBtn.slot = "footer";
    deleteBtn.variant = "danger";
    deleteBtn.autofocus = true;
    deleteBtn.tabIndex = 0;
    deleteBtn.innerHTML = getTranslation("general:common.delete");
    deleteBtn.addEventListener(
      "click",
      async () => {
        deleteBtn.loading = true;
        deleteBtn.disabled = true;
        const id =
          dta.userStatusType !== "EXTERNAL_PROVIDER"
            ? dta.sub
            : encodeURI(dta.username);

        await Services.organizations.adminDeleteOrganizationUser(
          orgId,
          dta.sub,
          id,
        );
        deleteBtn.loading = false;
        dialog.hide();
        this.displayOrganizationUsers();
      },
      false,
    );

    dialog.appendChild(htmlContent);
    dialog.appendChild(deleteBtn);
    this.appendChild(dialog);

    dialog.show();
  };
}

customElements.define(
  "tvd-my-organization-component",
  TVDMyOrganizationComponent,
);

declare global {
  interface HTMLElementTagNameMap {
    "tvd-my-organization-component": TVDMyOrganizationComponent;
  }
}

function createOrgUsersTable({
  data,
  userOptionsStorage,
  actionBox,
  onEditOrganizationUser,
  onDeleteOrganizationUser,
  userType,
  isReadOnly,
}: {
  data: ISingleOrganizationAndUsers;
  userOptionsStorage: IOptionsStorage<ICognitoUserWithOrgData>;
  actionBox: HTMLElement | undefined;
  onEditOrganizationUser: (org: ICognitoUserWithOrgData) => void;
  onDeleteOrganizationUser: (id: string, org: ICognitoUserWithOrgData) => void;
  userType: TUserRole | undefined;
  isReadOnly: boolean;
}): HTMLElement {
  const createSortIcon = (asc: boolean) => {
    const icon = document.createElement("sl-icon");
    icon.name = asc ? "sort-down-alt" : "sort-up-alt";
    return icon;
  };

  const organizationId = data.organization?.organizationId || "";

  const users = mergeUsersWithCurrentOrgData(organizationId, data.users || []);

  const fields: Array<ITableField<ICognitoUserWithOrgData>> = [
    {
      name: "name",
      label: getTranslation("general:common.name"),
    },
    {
      name: "familyName",
      label: getTranslation("general:users.familyName"),
    },
    {
      name: "email",
      label: getTranslation("general:common.email"),
    },
    {
      name: "organizationUserEnabled",
      label: getTranslation("general:common.enabled"),
      mapper: (u) =>
        getTranslation(
          `general:common.${u.organizationUserEnabled === "1" ? "yes" : "no"}`,
        ),
    },
    {
      name: "userStatusType",
      label: getTranslation("general:common.status"),
      mapper: (u) => getTranslation(`enums:UserStatusType.${u.userStatusType}`),
      className: (u) =>
        u?.userStatusType === "EXTERNAL_PROVIDER" ? "state-highlighted" : "",
    },
    {
      name: "organizationUserRole",
      label: getTranslation("general:common.type"),
      mapper: (u) =>
        getTranslation(`enums:UserTypeEnum.${u.organizationUserRole}`),
    },
    {
      name: "userCreateDate",
      label: getTranslation("general:common.createdAt"),
      valueFunction: (dta) =>
        dta.userCreateDate
          ? formatDateInCurrentTimezone(dta.userCreateDate)
          : "",
      className: "centered",
    },
    {
      name: "userLastModifiedDate",
      label: getTranslation("general:common.modifiedAt"),
      valueFunction: (dta) =>
        dta.userLastModifiedDate
          ? formatDateInCurrentTimezone(dta.userLastModifiedDate)
          : "",

      className: "centered",
    },
  ];

  if (userType !== "USER" && isReadOnly === false) {
    fields.push(
      {
        name: "sub",
        label: getTranslation("general:common.edit"),
        type: "icon",
        icon: (dta) =>
          userType === "SUPERADMIN" ||
          (userType === "ADMIN" && dta.planId !== "SUPERADMIN")
            ? "pencil"
            : "",
        notSortable: true,
        onClick: (dta) => {
          onEditOrganizationUser(dta);
        },
      },
      {
        name: "sub",
        label: getTranslation("general:common.delete"),
        type: "icon",
        icon: (dta) =>
          `USER#${securityModule.userSub}` !== dta.sub
            ? userType === "SUPERADMIN" ||
              (userType === "ADMIN" && dta.planId !== "SUPERADMIN")
              ? "trash"
              : ""
            : "",
        iconVariant: "danger",
        notSortable: true,
        onClick: (dta) => {
          onDeleteOrganizationUser(organizationId, dta);
        },
      },
    );
  }

  const tableUsers = new SmartTable<ICognitoUserWithOrgData>().initialize({
    className: "tvd-table tvd-table-smart-table",
    data: users || [],
    defaultSortBy: "name",
    title: data.organization?.name || "",
    fields,
    settings: {
      sortEnabled: true,
      sortVisible: true,
      userOptionsStorage,
      filters: {
        textFieldEnabled: true,
        textFieldFilterFields: ["name", "familyName", "email"],
        initialTextFieldFilter: userOptionsStorage.retrieveFilterText(),
      },
      labelSortBy: getTranslation("general:common.sortBy"),
      labelSearch: getTranslation("general:common.filterBy"),
      createSortIconFn: createSortIcon,
      actionBox,
    },
    pkFunction: (dta) => dta.sub,
  });

  return tableUsers;
}

function createUserEdit(
  submitButton: SlButton,
  orgId: string,
  prevData: ICognitoUserWithOrgData | undefined,
  showError: (e: ICustomError, data?: Record<string, unknown>) => void,
  allowedDomains: string[],
): IFormReturn<ICognitoUserWithOrgData> | null {
  const holder = document.createElement("div");

  const formValidator = z
    .object({
      email: z
        .string()
        .email()
        .transform((v) => v?.toLowerCase()),
      name: z.string().min(1),
      familyName: z.string().min(1),
      organizationUserEnabled: z.boolean(),
      organizationUserRole: z.nativeEnum(UserTypeEnum),
      mfa: z.boolean().optional(),
    })
    .refine(
      (data) => {
        if (data.email) {
          const domain = (data.email.split("@")[1] || "").toLowerCase().trim();
          if (allowedDomains.indexOf(domain) < 0) return false;
          return true;
        } else return false;
      },
      {
        path: ["email"],
      },
    ) as unknown as z.Schema<ICognitoUserWithOrgData>;

  const hasMfa = !!prevData?.mfa;
  const sameUser = prevData?.sub === securityModule.userSub;
  const userIsFederated =
    !!prevData && prevData.mainIdentityId !== prevData.sub;

  const federatedInfoNode = document.createElement("div");
  if (userIsFederated) {
    federatedInfoNode.innerHTML = getTranslation(
      "general:organizations.federations.disableUserEditsForFederatedUsers",
    );
  }

  const formFields: IFields<ICognitoUserWithOrgData> = [
    [
      {
        name: "name",
        label: "general:common.name",
        type: "textBox",
        disabled: userIsFederated,
        initialValue: prevData?.name || "",
      },
      {
        name: "familyName",
        label: "general:users.familyName",
        disabled: userIsFederated,
        type: "textBox",
        initialValue: prevData?.familyName || "",
      },
    ],
    [
      {
        name: "email",
        label: "general:common.email",
        type: "textBox",
        disabled: userIsFederated,
        initialValue: prevData?.email || "",
        helpText: getTranslation("general:users.domainsHelpText", {
          domains: allowedDomains.join(", ") || "-",
        }),
      },
      {
        name: "organizationUserEnabled",
        label: "general:common.enabled",
        type: "checkbox",
        helpText: sameUser
          ? "general:organizations.userCannotBeDeactivated"
          : "general:organizations.disableUserHelp",
        initialValue:
          prevData?.organizationUserEnabled === undefined
            ? true
            : prevData?.organizationUserEnabled === "1",
        padStart: true,
        disabled: sameUser,
      },
      securityModule.userHasPermission(systemRights.USER.CanDisableOthersMfa)
        ? {
            name: "mfa",
            label: "auth:mfa.activeMfa",
            helpText:
              !hasMfa || sameUser
                ? "auth:mfa.mfaCanOnlyBeActivatedBySameUser"
                : "auth:mfa.mfaDeactivateAction",
            disabled: !hasMfa || sameUser || userIsFederated,
            type: "checkbox",
            padStart: true,
            initialValue: hasMfa,
          }
        : undefined,
    ],
    createFieldOptionsForUserTypeEdit(
      securityModule.userRole || "USER",
      userIsFederated,
    ),
    {
      type: "node",
      node: federatedInfoNode,
      name: "",
    },
  ];

  const tedivoForm = new TedivoForm<ICognitoUserWithOrgData>({
    fields: formFields,
    onSubmit: () => undefined,
    formValidator,
    submitButton: submitButton,
    formProps: { autoFocusOnFirstInput: true },
    hiddenData: prevData,
  });

  translateTedivoForm<ICognitoUserWithOrgData>({
    tedivoForm,
    getTranslation: getTranslation,
  });

  holder.appendChild(tedivoForm.form);

  return {
    node: holder,
    tedivoForm,
    submitFunction: submitPassedToEditDrawer,
  };

  async function submitPassedToEditDrawer() {
    const validResult = tedivoForm.doSubmitForm();
    console.log(validResult);

    if (validResult.success) {
      const values = validResult.data;

      if (prevData?.sub) {
        const data: ICognitoUserWithOrgData = {
          ...prevData,
          ...values,
          organizationUserEnabled: values.organizationUserEnabled ? "1" : "0",
          organizationUserRole: values.organizationUserRole as TUserRole,
        };

        const resp = await Services.organizations.adminUpdateOrganizationUser(
          orgId,
          data,
        );

        if (!resp.data?.id) {
          showError(
            {
              errorCode: String(resp.statusCode),
              message: resp.code || "errorModifyingUser",
              translationKey: resp.message || "errors:errorModifyingUser",
            },
            data,
          );
          return false;
        }
      } else {
        const data: ICognitoUserWithOrgData = {
          ...validResult.data,
        };

        const resp = await Services.organizations.adminCreateOrganizationUser(
          orgId,
          data,
        );

        if (!resp.data?.id) {
          showError(
            {
              errorCode: String(resp.statusCode),
              message: resp.code || "errorCreatingUser",
              translationKey: resp.message || "errors:errorCreatingUser",
            },
            data,
          );
          return false;
        } else {
          if (resp.data?.merged) {
            createNotification(getTranslation("general:users.adminInvited"));
          }
        }
      }
      return true;
    } else {
      return false;
    }
  }

  function createFieldOptionsForUserTypeEdit(
    currentUserType: TUserRole,
    editUserIsFederated: boolean,
  ) {
    const options = [
      { value: "USER", label: getTranslation(`enums:UserTypeEnum.USER`) },
    ];

    if (currentUserType === "ADMIN" || currentUserType === "SUPERADMIN") {
      options.push({
        value: "ADMIN",
        label: getTranslation(`enums:UserTypeEnum.ADMIN`),
      });
    }

    if (currentUserType === "SUPERADMIN" && !editUserIsFederated) {
      options.push({
        value: "SUPERADMIN",
        label: getTranslation(`enums:UserTypeEnum.SUPERADMIN`),
      });
    }

    const editUserTypeField: IFieldForm<ICognitoUserWithOrgData> = {
      name: "organizationUserRole",
      label: "general:common.type",
      type: "radioButtonList",
      fieldset: true,
      initialValue: prevData?.organizationUserRole ?? "USER",
      options,
      disabled:
        options.length <= 1 ||
        (prevData?.organizationUserRole === "SUPERADMIN" &&
          currentUserType !== "SUPERADMIN"),
    };

    return editUserTypeField;
  }
}

function createOrganizationEditModal(
  submitButton: SlButton,
  showError: (e: ICustomError) => void,
  canChangeFederations: boolean,
  myOrgFederationsFn:
    | (() => Promise<IDomainFederation[] | undefined>)
    | undefined,
): IFormReturn<IEditOrganization> | null {
  const holder = document.createElement("div");

  let federationsNode: HTMLElement | undefined = undefined;

  if (canChangeFederations && myOrgFederationsFn) {
    federationsNode = document.createElement("div");

    createFederationsTable({
      federationsNode,
      myOrgFederationsFn,
    });
  }

  const formFields: IFields<IEditOrganization> = [
    [
      {
        name: "name",
        label: "general:common.organizationName",
        type: "textBox",
        initialValue: securityModule.currentOrganizationName || "",
      },

      {
        name: "mfaForAll",
        label: "auth:mfa.mfaForAllUsers",
        helpText: "auth:mfa.mfaForAllUsersInfo",
        type: "checkbox",
        padStart: true,
        initialValue: securityModule.organizationMfaForAllUsers,
      },
    ],
  ];

  if (federationsNode) {
    formFields.push({
      type: "title",
      label: "general:organizations.federations.title",
    });

    formFields.push({
      name: "federations",
      label: "general:organizations.federations.addFederation",
      type: "node",
      node: federationsNode,
    });
  }

  const formValidator = z.object({
    name: z.string().min(3),
    mfaForAll: z.boolean().optional(),
  }) as unknown as z.Schema<IEditOrganization>;

  const tedivoForm = new TedivoForm<IEditOrganization>({
    fields: formFields,
    onSubmit: () => undefined,
    formValidator,
    submitButton: submitButton,
    formProps: { autoFocusOnFirstInput: true },
  });

  translateTedivoForm<IEditOrganization>({
    tedivoForm,
    getTranslation: getTranslation,
  });

  holder.appendChild(tedivoForm.form);

  return {
    node: holder,
    tedivoForm,
    submitFunction: submitPassedToEditDrawer,
  };

  async function submitPassedToEditDrawer() {
    const validResult = tedivoForm.doSubmitForm();

    if (validResult.success) {
      const resp = await Services.accounts.changeOrganizationSelfDetails(
        validResult.data.name,
        validResult.data.mfaForAll || false,
      );

      if (resp.statusCode !== 200) {
        showError({
          errorCode: String(resp.statusCode),
          message: resp.code || "errorModifyingOrg",
          translationKey: resp.message || "errors:errorModifyingOrg",
        });
        return false;
      }

      securityModule.updateOrganizationDetails(
        validResult.data.name,
        validResult.data.mfaForAll || false,
      );

      return true;
    } else {
      return false;
    }
  }
}

function createFederationsTable({
  federationsNode,
  myOrgFederationsFn,
}: {
  federationsNode: HTMLElement;
  myOrgFederationsFn: () => Promise<IDomainFederation[] | undefined>;
}) {
  const divTable = new DivWithSpinner();
  divTable.className = "lids-table-mainHolder";

  federationsNode.appendChild(divTable);

  let federations: IDomainFederation[] = [];

  const smartTableConfig: ITable<IDomainFederation> = {
    data: federations,
    defaultSortBy: "domain",
    className:
      "tvd-table tvd-table-smart-table tvd-table-sticky tvd-table-sticky-0",
    fields: [
      {
        name: "domain",
        label: getTranslation("general:domains.domain"),
        type: "string",
        fixedWidth: "240px",
      },
      {
        name: "domainOwnershipVerified",
        type: "string",
        valueFunction: (dta) =>
          dta.domainOwnershipVerified
            ? getTranslation("general:common.yes")
            : getTranslation("general:common.no"),
        className: (dta) =>
          `centered ${dta?.domainOwnershipVerified ? "text-success" : ""}`,
        fixedWidth: "160px",
        label: getTranslation(
          "general:organizations.federations.domainOwnershipVerified",
        ),
      },
      {
        name: "enabled",
        type: "string",
        className: "centered",
        fixedWidth: "100px",
        valueFunction: (dta) =>
          dta.enabled
            ? getTranslation("general:common.yes")
            : getTranslation("general:common.no"),
        label: getTranslation("general:common.enabled"),
      },
      {
        name: "provider",
        type: "string",
        className: "centered",
        fixedWidth: "140px",
        valueFunction: (dta) =>
          dta.domainOwnershipVerified && dta.enabled
            ? dta.provider || "-"
            : "-",
        label: getTranslation("general:organizations.federations.provider"),
      },
      {
        name: "users",
        type: "string",
        valueFunction: (dta) =>
          dta.domainOwnershipVerified && dta.enabled
            ? dta.users?.length
              ? dta.users.join(", ")
              : getTranslation("general:organizations.federations.allUsers") ||
                "-"
            : "-",
        label: getTranslation("general:organizations.federations.users"),
      },
      {
        name: "id",
        className: "centered",
        label: getTranslation("general:common.actions"),
        type: "actions",
        iconVariant: "primary",
        icon: "three-dots-vertical",
        notSortable: true,
        fixedWidth: "60px",
        smallPadding: true,
        onClick: (
          dta: IDomainFederation,
          name: keyof IDomainFederation,
          table: SmartTable<IDomainFederation>,
          cmd: string | undefined,
        ) => {
          switch (cmd) {
            case "verifyDomain":
              verifyDomainOwnership(dta);
              break;
            case "enable":
              createEditEnableFederationDialog(
                "general:organizations.federations.actions.enable",
                dta,
                true,
              );
              break;
            case "disable":
              disableFederation(dta);
              break;
            case "selectUsers":
              createEditEnableFederationDialog(
                "general:organizations.federations.actions.selectUsers",
                dta,
                false,
              );
              break;
          }
        },
        options: (d) =>
          d.orgIdOwner !== securityModule.currentOrganizationId
            ? []
            : ([
                !d.domainOwnershipVerified
                  ? {
                      label: getTranslation(
                        "general:organizations.federations.actions.verifyDomain",
                      ),
                      value: "verifyDomain",
                      icon: "shield-check",
                    }
                  : undefined,
                d.domainOwnershipVerified && !d.enabled
                  ? {
                      label: getTranslation(
                        "general:organizations.federations.actions.enable",
                      ),
                      value: "enable",
                      icon: "file-check",
                    }
                  : undefined,
                d.domainOwnershipVerified && d.enabled
                  ? {
                      label: getTranslation(
                        "general:organizations.federations.actions.selectUsers",
                      ),
                      value: "selectUsers",
                      icon: "people",
                    }
                  : undefined,
                d.domainOwnershipVerified && d.enabled
                  ? {
                      label: getTranslation(
                        "general:organizations.federations.actions.disable",
                      ),
                      value: "disable",
                      icon: "radioactive",
                      variant: "danger",
                    }
                  : undefined,
              ].filter(Boolean) as ITableOnClickOptions[]),
      },
    ],
    settings: {
      filters: {
        textFieldEnabled: false,
      },
    },
    initialRows: 300,
    pkFunction: (dta) => `${dta.id}`,
  };

  const smartTable = new SmartTable<IDomainFederation>().initialize(
    smartTableConfig,
  );

  divTable.appendChild(smartTable);
  updateTableWithLatestData();

  return {
    divTable,
    smartTable,
  };

  function createFederationObjSchemaValidator() {
    const fedEditSchema: z.Schema<IEditIOrganizationFederation> = z.object({
      enabled: z.boolean(),
      domain: z.string().min(1),
      domainOwnershipVerificationToken: z.string().min(1),
      domainOwnershipVerified: z.boolean(),
      provider: z.nativeEnum(FederationProvidersEnum).optional(),
      users: z.string().optional(),
    });

    return fedEditSchema;
  }

  function createEditEnableFederationDialog(
    title: string,
    dta: IDomainFederation,
    selectProvider: boolean,
  ) {
    {
      const diag = new SlDialog();
      diag.label = getTranslation(title);
      diag.addEventListener("sl-after-hide", (event) => {
        if (event.target !== diag) return;
        diag.remove();
      });

      const okButton = new SlButton();
      okButton.innerHTML = getTranslation(title);
      okButton.variant = "primary";
      okButton.type = "submit";
      okButton.slot = "footer";

      const domainNode = document.createElement("div");
      domainNode.innerHTML = `<strong>${dta.domain}</strong>`;

      const { users, ...hiddenData } = dta;

      const tForm = new TedivoForm<IEditIOrganizationFederation>({
        fields: [
          {
            type: "node",
            name: "id",
            node: domainNode,
          },
          {
            name: "provider",
            label: getTranslation("general:organizations.federations.provider"),
            type: "select",
            disabled: !selectProvider,
            options: [
              {
                value: "",
                label: getTranslation("general:common.none"),
              },
              {
                value: FederationProvidersEnum.Microsoft,
                label: "Microsoft",
              },
            ],
            initialValue: dta.provider || "",
          },
          {
            name: "users",
            label: getTranslation("general:organizations.federations.users"),
            type: "textArea",
            placeholder: getTranslation(
              "general:organizations.federations.allUsers",
            ),
            rows: 5,
            initialValue: users?.filter(Boolean).join("\n") || "",
          },
        ],
        onSubmit: async (values) => {
          okButton.loading = true;
          okButton.disabled = true;

          await Services.domainFederations.ssoEnableFederation({
            ...dta,
            provider: values.provider as FederationProvidersEnum,
            users: (values?.users as string | undefined)
              ?.replace(/\r/g, "")
              .replace(/,/g, "\n")
              ?.split("\n")
              .filter(Boolean),
          });

          updateTableWithLatestData();

          diag.hide();
        },
        formValidator: createFederationObjSchemaValidator(),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        submitButton: okButton,
        formProps: { autoFocusOnFirstInput: true },
        hiddenData,
      });

      okButton.addEventListener("click", () => {
        tForm.doSubmitForm();
      });

      document.body.appendChild(diag);
      diag.appendChild(tForm.form);
      diag.appendChild(okButton);
      diag.show();
    }
  }

  function verifyDomainOwnership(dta: IDomainFederation) {
    const d = new IntegratedDialog<"ok" | "verify">({
      parentNode: document.body,
      buttonsAttrs: [
        {
          value: "close",
          variant: "primary",
          label: getTranslation("general:common.ok"),
          type: "button",
        },
        {
          value: "verify",
          variant: "success",
          label: getTranslation("general:common.verify"),
          type: "submit",
        },
      ],
      preventDefaultClose: true,
      removeDialogWhenHidden: true,
    });

    d.onButtonClicked = async (value, btn) => {
      if (value !== "verify") {
        d.hide();
        return true;
      }

      btn.loading = true;
      const resp = await Services.domainFederations.ssoCheckDnsTxt(dta.domain);
      btn.loading = false;
      d.hide();

      if (resp.statusCode !== 200) {
        createErrorNotification(
          getTranslation("errors:domainVerification.error"),
          10000,
        );
      } else {
        if (resp.data?.found === "OK") {
          updateTableWithLatestData();

          createNotification(
            getTranslation("general:organizations.federations.domainVerified", {
              domain: dta.domain,
            }),
            "success",
            "hand-thumbs-up",
            10000,
          );
        } else {
          createErrorNotification(
            getTranslation(
              `errors:domainVerification.error_${resp.data?.found}`,
              {
                domain: dta.domain,
                token: dta.domainOwnershipVerificationToken,
              },
            ),
            10000,
          );
        }
      }
      return true;
    };

    d.show(
      getTranslation("general:organizations.federations.actions.verifyDomain"),
      getTranslation(
        "general:organizations.federations.actions.verifyDomainInfo",
        {
          token: dta.domainOwnershipVerificationToken,
          domain: dta.domain,
        },
      ),
    );
  }

  function disableFederation(dta: IDomainFederation) {
    const d = new IntegratedDialog<"cancel" | "disable">({
      parentNode: document.body,
      buttonsAttrs: [
        {
          value: "cancel",
          variant: "primary",
          label: getTranslation("general:common.cancel"),
          type: "submit",
        },
        {
          value: "disable",
          variant: "danger",
          label: getTranslation("general:common.disable"),
          icon: "radioactive",
          type: "button",
        },
      ],
      preventDefaultClose: true,
      removeDialogWhenHidden: true,
    });

    d.onButtonClicked = async (action, btn) => {
      if (action === "cancel") {
        d.hide();
        return true;
      }

      btn.loading = true;
      await Services.domainFederations.ssoDisableFederation({
        domain: dta.domain,
      });
      btn.loading = false;
      d.hide();

      updateTableWithLatestData();
      return true;
    };

    d.show(
      getTranslation("general:organizations.federations.actions.disable"),
      getTranslation("general:organizations.federations.actions.disableInfo", {
        domain: dta.domain,
      }),
    );
  }

  function updateTableWithLatestData() {
    divTable.setLoading(true);

    myOrgFederationsFn().then((feds) => {
      if (feds) smartTable.updateData(feds);
      federations = feds || [];
      divTable.setLoading(false);
    });
  }
}

interface IEditOrganization extends Record<string, unknown> {
  name: string;
  mfaForAll: boolean;
  federations?: IDomainFederation[];
}

interface IEditIOrganizationFederation
  extends Omit<IDomainFederation, "users"> {
  users?: string;
}
